/**
 * Shared `Intl.DateTimeFormat` instances for app surfaces that render dates
 * in a consistent UTC, English-locale format (campaign / competition cards,
 * hero meta strips, etc.).
 *
 * Centralized here so that:
 *   - we don't recreate formatter instances per render (wasteful)
 *   - hub-v2 and Flow render dates identically
 */

/** Short date format: "Apr 22" */
export const SHORT_DATE_FORMAT = new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

/** Full date format with month, day, year: "Apr 22, 2027" */
export const FULL_DATE_FORMAT = new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * Compact date range that drops the year when start and end fall in the same
 * UTC year (e.g. "Apr 22 – May 23"), otherwise includes the year on the end
 * date only (e.g. "Dec 28 – Jan 4, 2027").
 */
export function formatShortDateRange(startInput: Date | string | number, endInput: Date | string | number): string {
  const start = startInput instanceof Date ? startInput : new Date(startInput);
  const end = endInput instanceof Date ? endInput : new Date(endInput);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new RangeError("formatShortDateRange received an invalid date input");
  }

  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();

  if (sameYear) return `${SHORT_DATE_FORMAT.format(start)} – ${SHORT_DATE_FORMAT.format(end)}`;
  return `${SHORT_DATE_FORMAT.format(start)} – ${FULL_DATE_FORMAT.format(end)}`;
}
